/**
 * Site look: 'floodlight' (v1.69+) or 'classic' (the look before Floodlight).
 * Functionality is identical in both; only styling differs.
 *
 *   site_config/ui_style  { default: 'floodlight' | 'classic' }   everyone (admins set it)
 *   users/{uid}.uiStyle   'floodlight' | 'classic' | absent        one person (admins set it)
 *
 * Effective look = a tab preview (?look=classic|floodlight, this tab only, writes nothing),
 * else the person's own setting, else the site default, else Floodlight.
 * The effective look is mirrored to <html data-ui-style> (CSS keys off it) and to
 * localStorage so the next visit paints the right look before Firestore answers.
 */
export const UI_STYLES = ['floodlight', 'classic'];
export const UI_STYLE_DEFAULT = 'floodlight';
export const UI_STYLE_KEY = 'bo.ui.style';
export const UI_STYLE_DOC = ['site_config', 'ui_style'];

export const validUiStyle = (s) => (UI_STYLES.includes(s) ? s : null);

export function resolveUiStyle(siteDefault, userOverride, preview = null) {
    return validUiStyle(preview) || validUiStyle(userOverride) || validUiStyle(siteDefault) || UI_STYLE_DEFAULT;
}

export const UI_PREVIEW_KEY = 'bo.ui.preview';

/**
 * Tab-only preview: `?look=classic` / `?look=floodlight` sets it, `?look=default` clears it.
 * Kept in sessionStorage so it survives in-app navigation in this tab.
 */
export function readUiPreview(search) {
    let q = null;
    try {
        q = new URLSearchParams(search ?? window.location.search).get('look');
    } catch {
        q = null;
    }
    try {
        if (q === 'default') { window.sessionStorage.removeItem(UI_PREVIEW_KEY); return null; }
        if (validUiStyle(q)) { window.sessionStorage.setItem(UI_PREVIEW_KEY, q); return q; }
        return validUiStyle(window.sessionStorage.getItem(UI_PREVIEW_KEY));
    } catch {
        return validUiStyle(q);
    }
}

export function clearUiPreview() {
    try { window.sessionStorage.removeItem(UI_PREVIEW_KEY); } catch { /* nothing stored */ }
    // Drop ?look= too, or a reload would turn the preview straight back on.
    try {
        const url = new URL(window.location.href);
        if (url.searchParams.has('look')) {
            url.searchParams.delete('look');
            window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
        }
    } catch { /* no URL to tidy */ }
}

/** A change this person made themselves (own toggle, ending a preview) applies at once for this long. */
export const LOCAL_LOOK_WINDOW_MS = 8000;

/**
 * The look to paint now. Once the look has settled, a change pushed from elsewhere
 * (an admin flips "Everyone", or this person's setting changes) waits for the next
 * screen change: swapping looks remounts the screen and would wipe a draft or legs.
 * Before it settles (site doc or profile still loading) and for this person's own
 * changes, it applies at once.
 */
export function nextShownStyle({ shown, live, settled, navigated, localChangeAt = 0, now = Date.now() }) {
    if (shown === live) return shown;
    if (!settled || navigated) return live;
    if (localChangeAt && now - localChangeAt < LOCAL_LOOK_WINDOW_MS) return live;
    return shown;
}

/** Display label, e.g. for the admin Users list. */
export const uiStyleLabel = (s) => (s === 'classic' ? 'Classic' : 'Floodlight');

export function readStoredUiStyle() {
    try {
        return validUiStyle(window.localStorage.getItem(UI_STYLE_KEY));
    } catch {
        return null;
    }
}

/** Mirror the effective look onto <html> and remember it for the next first paint. */
export function applyUiStyle(style, { remember = true } = {}) {
    const s = validUiStyle(style) || UI_STYLE_DEFAULT;
    if (typeof document !== 'undefined' && document.documentElement) {
        document.documentElement.setAttribute('data-ui-style', s);
    }
    if (!remember) return s;
    try {
        window.localStorage.setItem(UI_STYLE_KEY, s);
    } catch {
        // Private mode or blocked storage: this visit still gets the right look.
    }
    return s;
}

/** The look to paint before Firestore answers: the attribute index.html set, else storage, else default. */
export function initialUiStyle() {
    if (typeof document !== 'undefined') {
        const attr = validUiStyle(document.documentElement?.getAttribute?.('data-ui-style'));
        if (attr) return attr;
    }
    return readStoredUiStyle() || UI_STYLE_DEFAULT;
}
